export type ListingsSortState = { key: string; direction: 'asc' | 'desc' } | null;

export type ListingsReturnState = {
  version: 1;
  view: 'card' | 'list';
  page: number;
  search: string;
  statusFilter: string;
  approvalEverApproved: boolean;
  saleOrRentFilter: string;
  propertyTypeFilter: string;
  minPriceFilter: string;
  maxPriceFilter: string;
  minBedroomsFilter: string;
  minBathroomsFilter: string;
  petFriendlyFilter: boolean;
  poolFilter: boolean;
  gardenFilter: boolean;
  flatletFilter: boolean;
  retirementFilter: boolean;
  onShowFilter: boolean;
  auctionFilter: boolean;
  securityEstateFilter: boolean;
  repossessedFilter: boolean;
  showOptionalFilters: boolean;
  scopeActive: boolean;
  sort: ListingsSortState;
  windowScrollY: number;
  mainScrollTop: number;
};

const stringFields = [
  'search', 'statusFilter', 'saleOrRentFilter', 'propertyTypeFilter', 'minPriceFilter', 'maxPriceFilter',
  'minBedroomsFilter', 'minBathroomsFilter',
] as const;
const booleanFields = [
  'approvalEverApproved', 'petFriendlyFilter', 'poolFilter', 'gardenFilter', 'flatletFilter', 'retirementFilter',
  'onShowFilter', 'auctionFilter', 'securityEstateFilter', 'repossessedFilter', 'showOptionalFilters', 'scopeActive',
] as const;

export function createListingsReturnState(input: Omit<ListingsReturnState, 'version'>): ListingsReturnState {
  return { version: 1, ...input };
}

export function sellerActivityNavigationState(listingsReturnState: ListingsReturnState) {
  return { listingsReturnState };
}

export function listingsReturnLinkState(activityLocationState: unknown): { listingsReturnState: ListingsReturnState } | undefined {
  const listingsReturnState = readListingsReturnState(activityLocationState);
  return listingsReturnState ? { listingsReturnState } : undefined;
}

export function readListingsReturnState(locationState: unknown): ListingsReturnState | null {
  if (!locationState || typeof locationState !== 'object') return null;
  const candidate = (locationState as Record<string, unknown>).listingsReturnState;
  if (!candidate || typeof candidate !== 'object') return null;
  const value = candidate as Record<string, unknown>;
  if (value.version !== 1 || (value.view !== 'card' && value.view !== 'list')) return null;
  if (!Number.isInteger(value.page) || Number(value.page) < 1) return null;
  if (stringFields.some((field) => typeof value[field] !== 'string')) return null;
  if (booleanFields.some((field) => typeof value[field] !== 'boolean')) return null;
  if (!Number.isFinite(value.windowScrollY) || Number(value.windowScrollY) < 0) return null;
  if (!Number.isFinite(value.mainScrollTop) || Number(value.mainScrollTop) < 0) return null;
  const sort = value.sort;
  if (sort !== null && (!sort || typeof sort !== 'object'
    || typeof (sort as Record<string, unknown>).key !== 'string'
    || !['asc', 'desc'].includes(String((sort as Record<string, unknown>).direction)))) return null;
  return value as ListingsReturnState;
}
